import { CalendarOutlined, HomeOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Col, Input, Row, Select } from 'antd';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { extractTranslation } from '../../helpers/formatters';
import { highlightTitle } from '../../helpers/utils';
import {
  useGetPortalFacilityHierarchyQuery,
  useGetPortalUserAppointmentsQuery,
} from '../../src/generated/graphql';
import { AveliosButton } from '../common-layout/avelios-button';
import styles from './smart-search.module.scss';

const { Option, OptGroup } = Select;

interface AveliosOption {
  route: string;
  key: string;
  text: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
}

export const SmartSearch: FC = () => {
  const { formatMessage } = useIntl();

  const router = useRouter();
  const locale = router.locale;

  const [searchQuery, setSearchQuery] = useState<string>();

  const { data: appointmentsData, loading: appointmentsLoading } =
    useGetPortalUserAppointmentsQuery();

  const appointments = appointmentsData?.getPortalUserAppointments?.proxyAppointments;

  const previousOrganizationalUnits: AveliosOption[] | undefined = [];
  appointments
    ?.filter((appointmentProxy) =>
      moment(appointmentProxy?.appointment?.startTime?.seconds * 1000).isBefore(moment()),
    )
    ?.map((appointment) => appointment!.portalFacilityPath)
    .filter(
      (portalFacilityPath) =>
        !!portalFacilityPath?.portalFacilities && portalFacilityPath.portalFacilities.length > 0,
    )
    ?.map((portalFacilityPath) => {
      return portalFacilityPath!.portalFacilities![
        portalFacilityPath!.portalFacilities!.length - 1
      ];
    })
    ?.map((facility) => ({
      key: facility!.id!,
      icon: <HomeOutlined />,
      text: facility!.name!,
      description: extractTranslation(facility?.shortDescription, locale),
      route: `facility-selection?facilityId=${facility!.id!}`,
    }))
    .forEach((facility) => {
      if (!previousOrganizationalUnits.find((entry) => entry.key === facility?.key)) {
        previousOrganizationalUnits.push(facility!);
      }
    });

  const { data: facilitiesData, loading: facilitiesLoading } = useGetPortalFacilityHierarchyQuery({
    variables: { depth: 0 },
  });
  const facilityOptions: AveliosOption[] | undefined =
    facilitiesData?.getPortalFacilityHierarchy?.portalFacilities?.map((facility) => ({
      key: facility!.id!,
      icon: <HomeOutlined />,
      text: facility!.name!,
      description: extractTranslation(facility?.shortDescription, locale),
      route: `facility-selection?facilityId=${facility!.id!}`,
    }));

  const filterOptions = (options: AveliosOption[] | undefined): AveliosOption[] | undefined => {
    if (!searchQuery) {
      return options;
    }
    return options?.filter((option) =>
      option.text.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  };

  const handleOnSelect = (value: string) => {
    router.push(value);
  };

  const suggestions: AveliosOption[] = [
    {
      key: 'new-appointment',
      route: 'facility-selection',
      text: formatMessage({ id: 'smart-search.suggestion.new-appointment' }),
      icon: <PlusOutlined />,
    },
    {
      key: 'appointments',
      route: 'appointments',
      text: formatMessage({ id: 'smart-search.suggestion.my-appointments' }),
      icon: <CalendarOutlined />,
    },
    // {
    //   key: "documents",
    //   route: "documents",
    //   text: formatMessage({ id: "smart-search.suggestion.my-documents" }),
    //   icon: <FileOutlined />,
    // },
  ];

  const formatOption = (suggestion: AveliosOption, action?: boolean) => {
    return (
      <Option key={suggestion.key} value={suggestion.route} text={suggestion.text}>
        <Row className={action ? styles.optionAction : styles.option}>
          <Col span={24}>
            {suggestion.icon && <span className={styles.optionIcon}>{suggestion.icon}</span>}
            {highlightTitle(suggestion.text, searchQuery)}
          </Col>
        </Row>
        {suggestion.description && (
          <Row>
            <Col span={24} className={`${styles.optionDescription} description`}>
              {suggestion.description}
            </Col>
          </Row>
        )}
      </Option>
    );
  };

  const filteredSuggestions = useMemo(() => filterOptions(suggestions), [searchQuery, suggestions]);
  const filteredFacilities = useMemo(
    () => filterOptions(facilityOptions),
    [searchQuery, facilityOptions],
  );
  const filteredPrevious = useMemo(
    () => filterOptions(previousOrganizationalUnits),
    [searchQuery, previousOrganizationalUnits],
  );

  const loading = appointmentsLoading || facilitiesLoading;

  return (
    <Input.Group compact>
      <Select
        loading={loading}
        dropdownMatchSelectWidth={true}
        showSearch
        bordered={false}
        allowClear
        style={{ width: 'calc(100% - 200px)' }}
        suffixIcon={<SearchOutlined />}
        showArrow={false}
        searchValue={searchQuery}
        onSearch={setSearchQuery}
        autoClearSearchValue
        onSelect={handleOnSelect}
        className={styles.search}
        size={'large'}
        filterOption={false}
        maxLength={20}
        placeholder={formatMessage({ id: 'smart-search.placeholder' })}
      >
        <OptGroup label={formatMessage({ id: 'smart-search.suggestions' })}>
          {filteredSuggestions?.map((suggestion) => formatOption(suggestion, true))}
        </OptGroup>
        <OptGroup label={formatMessage({ id: 'smart-search.facilities' })}>
          {filteredFacilities?.map((facility) => formatOption(facility))}
        </OptGroup>
        {(filteredPrevious ?? []).length > 0 && (
          <OptGroup label={formatMessage({ id: 'smart-search.prev-facilities' })}>
            {filteredPrevious!.map((facility) => formatOption(facility))}
          </OptGroup>
        )}
      </Select>
      <div
        style={{
          backgroundColor: '#FFE388',
          borderRadius: '0 10px 10px 0',
          border: '0.5px solid black',
        }}
      >
        <AveliosButton
          loading={loading}
          style={{
            backgroundColor: '#FFE388',
            borderRadius: '0 10px 10px 0',
          }}
          size={'large'}
          icon={<SearchOutlined />}
        />
      </div>
    </Input.Group>
  );
};
